"use client";
import React, { createContext, useContext, useState, useCallback } from "react";
import { BASE_URL } from "../config/config";

const RatingContext = createContext();

export const RatingProvider = ({ children }) => {
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(false);

  const token = localStorage.getItem("token");

  const fetchRatings = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/ratings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && data.status === 200) {
        setRatings(data.data || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const addRating = async (payload) => {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    payload.create_audit_id = user.id;

    const res = await fetch(`${BASE_URL}/ratings`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    const data = await res.json();
    if (res.ok && data.status === 200) {
      await fetchRatings(); // Fetch fresh data from backend
      return true;
    } else {
      alert(data.message || "Add failed");
      return false;
    }
  } catch (err) {
    console.error(err);
    alert("Network error");
    return false;
  }
};

  const updateRating = async (id, payload) => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      payload.update_audit_id = user.id;

      const res = await fetch(`${BASE_URL}/ratings/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (res.ok && data.status === 200) {
        // Update local state immediately
        setRatings((prev) =>
          prev.map((r) => (r.id === id ? { ...r, ...payload } : r))
        );
        return true;
      } else {
        alert(data.message || "Update failed");
        return false;
      }
    } catch (err) {
      console.error(err);
      alert("Network error");
      return false;
    }
  };

  const deleteRating = async (id) => {
    if (!window.confirm("Are you sure you want to delete this rating?")) return;
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const res = await fetch(`${BASE_URL}/ratings/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ delete_audit_id: user.id }),
      });
      const data = await res.json();
      if (res.ok && data.status === 200) {
        setRatings((prev) => prev.filter((r) => r.id !== id));
      } else {
        alert(data.message || "Delete failed");
      }
    } catch (err) {
      console.error(err);
      alert("Delete failed");
    }
  };

  const toggleRatingStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === "Active" ? "Inactive" : "Active";
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const res = await fetch(`${BASE_URL}/ratings/${id}/status`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status: newStatus, update_audit_id: user.id }),
      });

      const data = await res.json();
      if (res.ok && data.status === 200) {
        setRatings((prev) =>
          prev.map((r) => (r.id === id ? { ...r, status: newStatus } : r))
        );
      } else {
        alert(data.message || "Status update failed");
      }
    } catch (err) {
      console.error(err);
      alert("Status update failed");
    }
  };

  return (
    <RatingContext.Provider
      value={{
        ratings,
        loading,
        fetchRatings,
        addRating,
        updateRating,
        deleteRating,
        toggleRatingStatus,
      }}
    >
      {children}
    </RatingContext.Provider>
  );
};

export const useRatings = () => useContext(RatingContext);
